import { VideoEditorLogger } from '../../utility/VideoEditorLogger';
import { VideoTranscriptElement } from '../models/elements/VideoTranscriptElement';
import { EditorChangeType } from '../types';
import { AbstractCommand } from './AbstractCommand';
import { TimelineMode } from './TimelineMode';
import { KaraokeTrackManager } from '../lib/KaraokeTrackManager';

import type { Element } from '../models/Element';
import type { VideoEditor } from '../VideoEditor';

/**
 * CropTimelineCommand
 * Keeps only media within [keepStart, keepEnd] (inclusive start, exclusive end semantics like typical timeline ranges).
 * Elements entirely outside range are removed.
 * Elements partially overlapping are trimmed (start and/or end) preserving relative media trim where possible.
 *
 * Undo restores removed elements and original properties for trimmed elements.
 */
export class CropTimelineCommand extends AbstractCommand {
  private keepStart: number;
  private keepEnd: number;
  private executed = false;
  private trackIdsToSkip: string[] = [];
  private originalTimelineDuration: number | null = null;
  private transcriptTrackModified = false;

  constructor(
    editor: VideoEditor,
    keepStart: number,
    keepEnd: number,
    tracksToSkip: string[] = [],
  ) {
    super(editor);
    this.editor = editor;
    this.keepStart = Math.max(0, keepStart);
    this.keepEnd = Math.max(this.keepStart, keepEnd);
    this.trackIdsToSkip = tracksToSkip;
  }

  getName(): string {
    return 'CropTimelineCommand';
  }

  getDescription(): string {
    return `Crop timeline to [${this.keepStart}, ${this.keepEnd}] seconds`;
  }

  execute(): boolean {
    if (this.executed) return false;

    super.execute(); // makes a snapshot

    try {
      VideoEditorLogger.logCommand(this, 'start', {
        keepStart: this.keepStart,
        keepEnd: this.keepEnd,
      });

      this.captureOriginalTimelineDuration();
      this.cropElements();
      this.shiftTracks();
      this.adjustTimelineDuration();

      this.editor.markDirty('crop_timeline_command');
      this.editor.notifyObservers(EditorChangeType.TIMELINE_CHANGED, {
        type: 'timeline_cropped',
        keepStart: this.keepStart,
        keepEnd: this.keepEnd,
      });

      // Reconcile karaoke track if transcript track was modified
      if (this.transcriptTrackModified) {
        const karaokeTrack = this.editor.getKaraokeTrack();
        if (karaokeTrack) {
          const karaokeTrackManager = new KaraokeTrackManager(this.editor);
          karaokeTrackManager.reconcileKaraokeTrack();
        }
      }

      this.executed = true;
      VideoEditorLogger.logCommand(this, 'complete', {
        keepStart: this.keepStart,
        keepEnd: this.keepEnd,
      });
      return true;
    } catch (error) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: error instanceof Error ? error.message : String(error),
      });
      return false;
    }
  }

  private captureOriginalTimelineDuration() {
    this.originalTimelineDuration = this.editor.timeline.duration ?? null;
  }

  private cropElements() {
    const timeline = this.editor.timeline;
    const tracks = [...timeline.tracks];

    const fixPrecision = (num: number) => {
      return String(num).includes('.') ? parseFloat(num.toFixed(4)) : num;
    };

    for (const track of tracks) {
      if (this.trackIdsToSkip.includes(track.id)) {
        continue;
      }
      for (const el of [...track.elements]) {
        // Outside entirely on left or right
        if (
          fixPrecision(el.end_time) <= fixPrecision(this.keepStart) ||
          fixPrecision(el.time) >= fixPrecision(this.keepEnd)
        ) {
          if (el instanceof VideoTranscriptElement) {
            this.transcriptTrackModified = true;
          }
          timeline.removeElement(el.id);
        } else {
          this.handleOverlapping(el);
        }
      }
      if (track.elements.length === 0) {
        timeline.removeTrack(track.id);
      }
    }
  }

  private adjustTimelineDuration() {
    const newTimelineDuration = this.keepEnd - this.keepStart;
    if (newTimelineDuration >= 0) {
      this.editor.timeline.setDuration(newTimelineDuration);
    }
  }

  private handleOverlapping(el: Element) {
    const elStart = el.time;
    const elEnd = el.end_time;
    const newStart = Math.max(elStart, this.keepStart);
    const newEnd = Math.min(elEnd, this.keepEnd);
    if (el instanceof VideoTranscriptElement) {
      const trackManager = this.editor.getTranscriptTrackManager();
      if (newStart !== elStart) {
        trackManager.trimElementStart(el, newStart);
        this.transcriptTrackModified = true;
      }
      if (newEnd !== elEnd) {
        trackManager.trimElementEnd(el, newEnd);
        this.transcriptTrackModified = true;
      }
    } else {
      const trackManager = this.editor.getTrackManager();
      if (newStart !== elStart) {
        trackManager.trimElementStart(el, newStart);
      }
      if (newEnd !== elEnd) {
        trackManager.trimElementEnd(el, newEnd);
      }
    }
  }

  private shiftTracks() {
    // get a fresh list of tracks after potential removals above
    const tracksToShift = this.editor.timeline.tracks.filter(
      t => !this.trackIdsToSkip.includes(t.id),
    );
    for (const track of tracksToShift) {
      const firstEl = track.sortedElements[0];
      if (firstEl instanceof VideoTranscriptElement) {
        this.editor
          .getTranscriptTrackManager()
          .shiftElement(
            firstEl,
            firstEl.time - this.keepStart,
            TimelineMode.RIPPLE,
            false,
          );
      } else if (firstEl) {
        this.editor
          .getTrackManager()
          .shiftElement(
            firstEl,
            firstEl.time - this.keepStart,
            TimelineMode.RIPPLE,
            false,
          );
      } else {
        VideoEditorLogger.logTimelineOperation(
          'crop_timeline_command__shift_tracks_warning',
          {
            error: 'Unexpected: track with no elements after removal',
            track,
          },
        );
      }
    }
  }

  undo(): boolean {
    if (!this.executed) {
      return false;
    }

    const result = super.undo();

    // Restore original duration explicitly (snapshot restore may have recalculated based on shifted elements)
    if (result && this.originalTimelineDuration !== null) {
      try {
        this.editor.timeline.setDuration(this.originalTimelineDuration);
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        VideoEditorLogger.logTimelineOperation(
          'crop_timeline_command__restore_duration_fail',
          {
            error: err.message,
          },
        );
      }
    }

    VideoEditorLogger.logCommand(this, 'complete', {
      operation: 'undo',
      result,
    });

    return result;
  }
}
